import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, InlineLabeledRadio} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import '../../static/css/modal.less';
import transcriptionApi from '../../service/transcriptionApi';
import {SetupWorkerState} from '../../model/workerState';
import {StyledWarning, MinWidthButtonWrapper} from '../util/StyledElements';
import captionApi from '../../service/captionApi';
import styled from 'styled-components';
import config from '../../service/config';
import {getCaptionEditorUrl} from '../../util/UrlUtil';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {mediaStore} from '../../context/mediaProvider';
import {MediaPrivacy} from '../../model/mediaDetails';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import {EntitlementType} from '../../model/userQuotas';
import {useUserQuotaStore} from '../../context/userQuotaStore';

const AddCaptionTrackModalBody = styled.div`
   font-size: 0.875rem;
`;

export const AddCaptionTrackModalBase: React.FC<IAddCaptionTrackModalProps & IStateMappedProps> = ({refreshCaptionData, onClose, MediaId, Hash, XmpId, Privacy}) => {
   const {t} = useTranslation();
   const quotaEntitlementType = useUserQuotaStore(s => s.QuotaEntitlementType);
   const groupId = 'add-caption-radios';

   const options = {
      generate: 'generate',
      custom: 'custom',
      thirdParty: 'thirdParty'
   };
   const [selectedOption, setSelectedOption] = useState(options.generate);
   const [primaryButtonText, setPrimaryButtonText] = useState<string>(t('general.apply'));
   const [workerState, setWorkerState] = SetupWorkerState();

   const onSelectGenerate = () => {
      setWorkerState({isWorking: false, hasError: false});
      setSelectedOption(options.generate);
      setPrimaryButtonText(t('general.apply'));
   };

   const onSelectCustom = () => {
      setWorkerState({isWorking: false, hasError: false});
      setSelectedOption(options.custom);
      setPrimaryButtonText(t('general.create'));
   };
   const onSelectThirdParty = () => {
      setWorkerState({isWorking: false, hasError: false});
      setSelectedOption(options.thirdParty);
      setPrimaryButtonText(t('general.request'));
   };

   const saveXmpCaptions = async () => {
      const captionTrack = await captionApi.getCaptionTrack(MediaId);
      await captionApi.saveCaptionTrack(MediaId, captionTrack);
   };

   const onClickApply = async () => {
      try {
         setWorkerState({isWorking: true, hasError: false});
         switch (selectedOption) {
            case options.generate: {
               await transcriptionApi.startAutomatedTranscription(Hash);
               break;
            } case options.thirdParty: {
               await transcriptionApi.startThirdPartyTranscription(Hash);
               break;
            } default: {
               XmpId && await saveXmpCaptions();
               window.location.href = getCaptionEditorUrl(Hash);
            }
         }
         setWorkerState({isWorking: false, hasError: false});
         refreshCaptionData();
         onClose();
      } catch {
         setWorkerState({isWorking: false, hasError: true});
      }
   };

   const buttonGroup: React.JSX.Element = (
      <div className="button-group">
         <MinWidthButtonWrapper>
            <ControlButton
               label={t('general.cancel')}
               buttonType={TscButtonType.secondary}
               onClick={onClose}
               testId="change-assignee-cancel-button"
            />
            <ControlButton
               label={primaryButtonText}
               buttonType={TscButtonType.primary}
               onClick={onClickApply}
               testId="change-assignee-apply-button"
            />
         </MinWidthButtonWrapper>
      </div>
   );

   const showAutomaticTranscription = !config.featureSwitches.DisableAutomaticTranscription && (!config.featureSwitches.IsScreencast || (config.featureSwitches.IsScreencast && quotaEntitlementType !== EntitlementType.free));

   return (
      <BasicModalPortal
         testId="add-caption-track-modal"
         title={t('accessibility.captions.modal.title')}
         width={cssConstants.defaultModalWidth}
         onClose={onClose}
         isLoading={workerState.isWorking}
      >
         <AddCaptionTrackModalBody className="modal-body">
            <div className="form-body themeable-section">
               {workerState.hasError && (
                  <StyledWarning>
                     {t('accessibility.captions.modal.error')}
                  </StyledWarning>
               )}
               {showAutomaticTranscription && (
                  <InlineLabeledRadio
                     groupId={groupId}
                     label={t('accessibility.captions.modal.generateTitle')}
                     description={t('accessibility.captions.modal.generateMessage')}
                     testId="add-caption-modal-generate"
                     onChange={onSelectGenerate}
                     isSelected={selectedOption === options.generate}
                  />
               )}
               <InlineLabeledRadio
                  groupId={groupId}
                  label={t('accessibility.captions.modal.customTitle')}
                  description={t('accessibility.captions.modal.customMessage')}
                  testId="add-caption-modal-custom"
                  onChange={onSelectCustom}
                  isSelected={selectedOption === options.custom}
               />
               {config.featureSwitches.EnableThirdPartyCaptionGeneration && (
                  <InlineLabeledRadio
                     groupId={groupId}
                     label={Privacy === MediaPrivacy.private ? t('accessibility.captions.modal.thirdPartyTitleWithPrivateMedia') : t('accessibility.captions.modal.thirdPartyTitle')}
                     description={t('accessibility.captions.modal.thirdPartyMessage')}
                     testId="add-caption-modal-third-party"
                     onChange={onSelectThirdParty}
                     isSelected={selectedOption === options.thirdParty}
                     disabled={Privacy === MediaPrivacy.private}
                  />
               )}
               {buttonGroup}
            </div>
         </AddCaptionTrackModalBody>
      </BasicModalPortal>
   );
};

export interface IAddCaptionTrackModalProps {
   refreshCaptionData(): void;
   onClose(): void;
}

export interface IStateMappedProps {
   MediaId: string;
   Hash: string;
   XmpId: string;
   Privacy: MediaPrivacy;
}

const mapStatesToProps = (
   {refreshCaptionData, onClose}: IAddCaptionTrackModalProps,
   {MediaId, Hash, XmpId, Privacy}: Partial<IStateMappedProps>
): IStateMappedProps & IAddCaptionTrackModalProps => ({refreshCaptionData, onClose, MediaId, Hash, XmpId, Privacy});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(AddCaptionTrackModalBase);
